import { MessageBarType } from '@fluentui/react';
import { useMediaQuery, useToast } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Alias, AppInstance, AppInstance_Visibility, UpdateAppInstanceRequest } from '../../ai.h2o.cloud.appstore';
import { defaultAppstoreWorkspaceName } from '../../authz/authz.hooks';
import { useWorkspaces } from '../../pages/Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../../pages/RoutePaths';
import { AdminAliasService, AdminAppService, AppService } from '../../services/api';
import { useError, useInstance } from '../../utils/hooks';
import { InstanceListType } from '../../utils/models';
import { handleErrMsg, InstancePauseResumeMap, InstancePauseResumeOpEnum } from '../../utils/utils';
import { InstanceList } from '../InstanceList/InstanceList';
import ListPage from '../ListPages/ListPage';
import { InstanceSearchAndFilter } from './InstanceSearchAndFilter';

interface InstanceListPageProps {
  type: InstanceListType;
}

export default function InstanceListPage(props: InstanceListPageProps) {
  const { ACTIVE_WORKSPACE_NAME = defaultAppstoreWorkspaceName, activeWorkspaceInitialized } = useWorkspaces();
  const { type } = props,
    history = useHistory(),
    { isDesktop } = useMediaQuery(),
    { activeWorkspace } = useWorkspaces(),
    [activeWorkspaceName, setActiveWorkspaceName] = useState<string>(),
    [instances, setInstances] = useState<AppInstance[]>(),
    [refinedInstances, setRefinedInstances] = useState<AppInstance[]>(),
    [aliases, setAliases] = useState<Alias[]>(),
    [loading, setLoading] = useState(false),
    [loadingInstance, setLoadingInstance] = useState<[string, boolean]>(),
    [terminatingInstance, setTerminatingInstance] = useState<[string, boolean]>(),
    [err, setErr] = useError(),
    { getInstancesList, getAdminInstancesList, setInstanceSuspension, terminateInstance } = useInstance(),
    { addToast } = useToast(),
    isAdmin = type === InstanceListType.admin,
    getUserInstanceList = isAdmin ? getAdminInstancesList : getInstancesList,
    loadInstances = useCallback(
      async (id?: string, parent = activeWorkspaceName) => {
        if (parent === undefined) return;
        if (!id) setLoading(true);
        try {
          if (isAdmin) {
            const { aliases } = await AdminAliasService.listAliases({
              instanceId: '',
              parent,
            });
            setAliases(aliases);
          }
          const appInstances = await getUserInstanceList({
            appId: '',
            includeAppDetails: true,
            visibility: AppInstance_Visibility.VISIBILITY_UNSPECIFIED,
            allUsers: isAdmin,
            allWorkspaces: isAdmin,
            parent,
          });
          setInstances(appInstances);
        } catch (error: unknown) {
          if (error instanceof Error) setErr({ ...error });
        } finally {
          setLoading(false);
        }
      },
      [setErr, getInstancesList, isAdmin, activeWorkspaceName]
    ),
    setSuspension = useCallback(
      (appInstance: AppInstance, opEnum: InstancePauseResumeOpEnum) => async () => {
        const { id, parent } = appInstance;
        const op = InstancePauseResumeMap.get(opEnum);
        if (!op) return;
        setLoadingInstance([id, true]);
        try {
          await setInstanceSuspension(appInstance, op, isAdmin, parent);
          await loadInstances(id, activeWorkspaceName);

          addToast({
            message: `${op.completedDescription} app instance with id ${id}`,
            messageBarType: MessageBarType.success,
          });
        } catch (error: any) {
          console.error(error);
        } finally {
          setLoadingInstance([id, false]);
        }
      },
      [loadInstances, setInstanceSuspension, activeWorkspaceName, isAdmin]
    ),
    terminate = useCallback(
      (instance: AppInstance) => async () => {
        const { id } = instance;
        setTerminatingInstance([id, true]);
        try {
          await terminateInstance(instance, isAdmin, instance.parent);
          await loadInstances(id, activeWorkspaceName);
          addToast({ message: `Terminated app instance with id ${id}`, messageBarType: MessageBarType.success });
        } catch (error: any) {
          addToast({
            message: `Could not terminate app instance: ${handleErrMsg(error?.message as string)}`,
            messageBarType: MessageBarType.error,
          });
        } finally {
          setTerminatingInstance([id, false]);
        }
      },
      [loadInstances, terminateInstance, activeWorkspaceName, isAdmin]
    ),
    updateVisibility = useCallback(
      (updatedInstance: UpdateAppInstanceRequest) => async () => {
        if (activeWorkspaceName === undefined) return;
        const updateInstanceParams: UpdateAppInstanceRequest = {
          ...updatedInstance,
          parent: activeWorkspaceName,
        };
        setLoadingInstance([updatedInstance.id, true]);
        try {
          if (isAdmin) {
            await AdminAppService.updateAppInstance(updateInstanceParams);
          } else if (type === InstanceListType.my) {
            await AppService.updateAppInstance(updateInstanceParams);
          }
          await loadInstances(updatedInstance.id, activeWorkspaceName);
          addToast({
            message: `Edited app instance with id ${updatedInstance.id}`,
            messageBarType: MessageBarType.success,
          });
        } catch (error: any) {
          addToast({
            message: `Could not update app instance visibility: ${handleErrMsg(error?.message as string)}`,
            messageBarType: MessageBarType.error,
          });
        } finally {
          setLoadingInstance([updatedInstance.id, false]);
        }
      },
      [loadInstances, addToast, activeWorkspaceName, isAdmin]
    );
  useEffect(() => {
    if (!activeWorkspaceInitialized || loading) return;
    setActiveWorkspaceName(ACTIVE_WORKSPACE_NAME);
    setRefinedInstances([]);
    void loadInstances(undefined, ACTIVE_WORKSPACE_NAME);
  }, [loadInstances, ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized, isAdmin]);

  return (
    <ListPage
      copy={{
        title: isAdmin ? 'Admin App Instances' : type === InstanceListType.my ? 'Workspace App Instances' : '',
        subtitle: (() => {
          const instanceCount = refinedInstances?.length || 0;
          const subject = isAdmin ? 'There are' : 'You have';

          if (loading) return 'Loading instances...';

          let workspaceText = '';
          if (isAdmin) {
            workspaceText = ' in all workspaces';
          } else if (activeWorkspace?.displayName) {
            workspaceText = ` in the "${activeWorkspace.displayName}" workspace`;
          }

          return `${subject} ${instanceCount} instances${workspaceText}`;
        })(),
        noDataTitle: 'No instances found',
        noDataMessage: '',
        loadingMessage: 'Loading App Instances...',
        noFilterResultsMessage: 'No instances match these filters',
        searchPlaceholder: isDesktop ? 'Search by app name, title, ID, version, description' : 'Search Engine',
      }}
      error={err?.message}
      loading={!activeWorkspaceInitialized || loading}
      primaryButtonProps={
        isAdmin
          ? {
              onClick: () => {
                history.push(RoutePaths.ADMIN_ALIASES);
              },
              text: 'Manage Aliases',
            }
          : undefined
      }
      showSearchInput={false}
      showNoDataPage={instances?.length === 0}
    >
      <InstanceSearchAndFilter instances={instances || []} onRefine={(data) => setRefinedInstances(data)} />
      <InstanceList
        aliases={isAdmin ? aliases : undefined}
        instances={refinedInstances}
        loadingInstance={loadingInstance}
        terminatingInstance={terminatingInstance}
        type={type}
        terminateInstance={terminate}
        setInstanceSuspension={setSuspension}
        updateVisibility={updateVisibility}
      />
    </ListPage>
  );
}
